import {
  bankAuthenticationFailedCodes,
  billingOrContactDetailsInvalidCodes,
  incorrectCardDetailsCodes,
  insufficientFundsCodes,
  paymentCancelledOrTimedOutCodes,
  paymentDeclinedByBankCodes,
  paymentProcessingCodes,
  paymentUnderReviewCodes,
  savedCardUnavailableCodes,
  systemOrConfigurationErrorCodes,
  temporaryConnectionOrServiceProblemCodes,
  tooManyAttemptsCodes,
  unsupportedPaymentMethodCodes,
} from './peach.codes';

export const waitForCheckoutSDK = async (maxAttempts = 20, delayMs = 250): Promise<boolean> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if ((window as any).Checkout) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return false;
};

export const waitForElement = async (selector: string, maxAttempts = 50, delayMs = 100): Promise<Element> => {
  let attempt = 0;
  while (attempt < maxAttempts) {
    const element = document.querySelector(selector);
    if (element) {
      return element;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    attempt++;
  }
  throw new Error(`Element "${selector}" not found after ${maxAttempts} attempts.`);
};

export type PeachCheckoutEvent = {
  amount: number;
  checkoutId: string;
  currency: string;
  merchantTransactionId: string;
  paymentType: 'DB' | 'PA';
  result: {
    code: string;
    description: string;
  };
  signature: string;
  timestamp: Date;
};

export type PeachCompletedCheckoutEvent = PeachCheckoutEvent & {
  merchant: {
    name: string;
  };
  paymentBrand: string;
  resultDetails: {
    AcquirerResponse: string;
    ConnectorTxID1: string;
    ExtendedDescription: string;
  };
};

export type PeachExpiredCheckoutEvent = PeachCheckoutEvent & {
  id: string;
};

export type PeachErrorCheckoutEvent = {
  result: {
    code: string;
    description: string;
  };
};

export type PaymentMessage = {
  title: string;
  message: string;
};

export type PeachErrorAction = 'checkout' | 'paymentMethod' | 'customerDetails' | 'paymentStatus' | 'support';

export const getPeachErrorAction = (code: string): PeachErrorAction | undefined => {
  const actionGroups: [PeachErrorAction, string[]][] = [
    ['checkout', [...bankAuthenticationFailedCodes, ...incorrectCardDetailsCodes, ...savedCardUnavailableCodes]],
    [
      'paymentMethod',
      [
        ...insufficientFundsCodes,
        ...unsupportedPaymentMethodCodes,
        ...paymentDeclinedByBankCodes,
        ...paymentCancelledOrTimedOutCodes,
      ],
    ],
    ['customerDetails', billingOrContactDetailsInvalidCodes],
    [
      'paymentStatus',
      [
        ...paymentUnderReviewCodes,
        ...paymentProcessingCodes,
        ...tooManyAttemptsCodes,
        ...temporaryConnectionOrServiceProblemCodes,
      ],
    ],
    ['support', systemOrConfigurationErrorCodes],
  ];

  return actionGroups.find(([, codes]) => codes.includes(code))?.[0];
};

const createPaymentMessages = (codes: string[], title: string, message: string): Record<string, PaymentMessage> =>
  Object.fromEntries(
    codes.map((code) => [
      code,
      {
        title,
        message,
      },
    ]),
  );

export const paymentErrorMessages: Record<string, PaymentMessage> = {
  ...createPaymentMessages(
    paymentUnderReviewCodes,
    "Payment received - we're confirming your order",
    "We've received your payment and are completing a final check. We'll email you as soon as your order is confirmed. Please don't pay again.",
  ),

  ...createPaymentMessages(
    paymentProcessingCodes,
    "We're still processing your payment",
    "This can take a few moments. Please keep this page open and don't try to pay again.",
  ),

  ...createPaymentMessages(
    bankAuthenticationFailedCodes,
    "We couldn't verify your payment with your bank",
    'Please try again and complete the verification step requested by your bank, such as the one-time PIN or banking-app approval. If this keeps happening, contact your bank or use a different card.',
  ),

  ...createPaymentMessages(
    incorrectCardDetailsCodes,
    'Please check your card details',
    'Some of the card details entered could not be verified. Please check the card number, expiry date and security code (CVV), then try again.',
  ),

  ...createPaymentMessages(
    insufficientFundsCodes,
    'Your payment was declined',
    "Your bank couldn't approve this payment. Please check your available balance or card limits, or use a different card.",
  ),

  ...createPaymentMessages(
    billingOrContactDetailsInvalidCodes,
    "We couldn't verify your details",
    'Please check that your billing address and contact details are correct and match the information held by your bank, then try again.',
  ),

  ...createPaymentMessages(
    unsupportedPaymentMethodCodes,
    "This card or payment method isn't supported",
    'Please choose a different card or payment method to continue.',
  ),

  ...createPaymentMessages(
    savedCardUnavailableCodes,
    "We couldn't use your saved card",
    'Please enter your card details again, or use a different card, to continue.',
  ),

  ...createPaymentMessages(
    paymentDeclinedByBankCodes,
    'Your payment was declined',
    "Your bank couldn't approve this payment. Please use a different card or payment method, or contact your bank for more information.",
  ),

  ...createPaymentMessages(
    tooManyAttemptsCodes,
    'Please wait before trying again',
    "We've received several payment attempts for this checkout. Please wait a few minutes before trying again. If you've already completed a payment, don't submit another one.",
  ),

  ...createPaymentMessages(
    paymentCancelledOrTimedOutCodes,
    'Payment not completed',
    'The payment was cancelled or the session timed out. Your checkout details have been saved, so you can try again when you’re ready.',
  ),

  ...createPaymentMessages(
    temporaryConnectionOrServiceProblemCodes,
    "We couldn't complete your payment",
    "We didn't receive a response from the payment service. Please wait while we confirm the payment status. Don't submit another payment while we check.",
  ),

  ...createPaymentMessages(
    systemOrConfigurationErrorCodes,
    "We can't process your payment right now",
    "We're unable to complete this payment at the moment. Please try again later or contact support.",
  ),
};
